/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { decorate, observable, action, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Icon } from 'semantic-ui-react';

/**
 * A dropdown component that groups workspace actions (Start, Stop, Change Instance Type, Terminate)
 * under a single expandable menu on the built-in EnvironmentCard.
 *
 * Props:
 *  - environment: the Environment model instance
 *  - environmentsStore: the built-in EnvironmentsStore
 *  - user: the current user object
 *  - onChangeInstanceType: callback invoked when "Change Instance Type" is selected
 *  - onTerminate: callback invoked when "Terminate" is selected
 */
class EnvironmentActionsDropdown extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.dropdownOpen = false;
    });
  }

  setDropdownOpen = (state) => {
    runInAction(() => {
      this.dropdownOpen = state;
    });
  };

  handleStop = async () => {
    this.setDropdownOpen(false);
    const { environmentsStore, environment } = this.props;
    await environmentsStore.stopEnvironment(environment);
  };

  handleStart = async () => {
    this.setDropdownOpen(false);
    const { environmentsStore, environment } = this.props;
    await environmentsStore.startEnvironment(environment);
  };

  handleChangeInstanceType = () => {
    this.setDropdownOpen(false);
    this.props.onChangeInstanceType();
  };

  handleTerminate = () => {
    this.setDropdownOpen(false);
    this.props.onTerminate();
  };

  render() {
    const { environment, user } = this.props;
    const { status } = environment;
    const isModifying = status === 'MODIFYING';
    const isCompleted = status === 'COMPLETED';
    const isStopped = status === 'STOPPED';
    const isExternalAdmin = user.isAdmin && environment.isExternal;

    const validEnvTypes = ['ec2-windows', 'ec2-linux', 'ec2-rstudio', 'sagemaker'];
    const supportsStartStop = validEnvTypes.includes(environment.instanceInfo.type);

    const items = [];

    if (isCompleted) {
      if (supportsStartStop && !isExternalAdmin) {
        items.push({ key: 'stop', icon: 'power', iconColor: '#ea580c', label: 'Stop', onClick: this.handleStop });
      }
      items.push({ key: 'terminate', icon: 'minus circle', iconColor: '#dc2626', label: 'Terminate', onClick: this.handleTerminate });
    }

    if (isStopped) {
      if (!isExternalAdmin) {
        items.push({ key: 'start', icon: 'power', iconColor: '#16a34a', label: 'Start', onClick: this.handleStart });
        items.push({ key: 'change-instance-type', icon: 'exchange', iconColor: '#2563eb', label: 'Change Instance Type', onClick: this.handleChangeInstanceType });
      }
      items.push({ key: 'terminate', icon: 'minus circle', iconColor: '#dc2626', label: 'Terminate', onClick: this.handleTerminate });
    }

    if (items.length === 0) {
      return null;
    }

    return (
      <div className="workspace-actions-dropdown-wrapper">
        <button
          className="workspace-action-btn workspace-action-btn-secondary"
          disabled={isModifying}
          onClick={(e) => { e.stopPropagation(); this.setDropdownOpen(!this.dropdownOpen); }}
        >
          {isModifying
            ? <Icon name="spinner" loading style={{ lineHeight: 1, height: '1em', margin: 0 }} />
            : <Icon name="setting" style={{ lineHeight: 1, height: '1em', margin: 0 }} />
          }
          {' '}Actions{' '}
          <Icon name="caret down" style={{ lineHeight: 1, height: '1em', margin: '0 0 0 2px' }} />
        </button>
        {this.dropdownOpen && (
          <>
            <div className="workspace-actions-dropdown-backdrop" onClick={() => this.setDropdownOpen(false)} />
            <div className="workspace-actions-dropdown-menu">
              {items.map(item => (
                <button
                  key={item.key}
                  className="workspace-actions-dropdown-item"
                  onClick={item.onClick}
                >
                  <Icon name={item.icon} style={{ lineHeight: 1, height: '1em', margin: 0, color: item.iconColor }} />
                  {item.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }
}

decorate(EnvironmentActionsDropdown, {
  dropdownOpen: observable,
  setDropdownOpen: action,
});

export default observer(EnvironmentActionsDropdown);
